import React, {useEffect, useMemo, useRef} from 'react';
import echarts from '@/utils/echartsCore';
import {DataLabelPosition} from 'entities/chart';
import {ChartType} from '@/views/storyboard/utils/chartConstants';
import {getDefaultDataLabelPosition} from '@/views/storyboard/utils/chartDataLabels';
import {
    buildPieInsideValueLabelConfig,
    buildPieLabelLineConfig,
    buildPieOutsideNameAndValueLabelConfig,
    buildPieOutsideNameLabelConfig,
    pieChartUsesInsideValueOverlay,
} from '@/views/storyboard/utils/chartDataLabels';
import {buildChartImageDownloadToolboxFeatures} from '@/views/storyboard/utils/chartImageDownload';
import i18n from 'i18n';

export interface PieChartDataItem {
    name?: string | number;
    value?: number;
    share?: string;
    total?: number;
}

export interface PieChartProps {
    data: PieChartDataItem[];
    height?: number;
    width?: number;
    chartSettingsView?: boolean;
    title?: string;
    dataLabelPosition?: DataLabelPosition;
    description?: string;
    datePeriod?: string;
}

const PieCharts: React.FC<PieChartProps> = (
    {
        data,
        height = 1,
        width = 1,
        chartSettingsView = false,
        title = '',
        dataLabelPosition = getDefaultDataLabelPosition(ChartType.PieChart),
        description = '',
        datePeriod = '',
    }
) => {

    const chartRef = useRef(null);

    const showToolbox = !chartSettingsView && height !== 1;

    const {
        showLegend,
        chartRadius,
        chartCenter,
        labelsToShow,
        startAngle,
        fontSize,
    } = useMemo(() => {
        let showLegend = true;
        let chartRadius = ['30%', '60%'];
        let chartCenter = ['50%', '40%'];
        let labelsToShow = 55;
        let startAngle = 90;
        let fontSize = 13;

        if (chartSettingsView) {
            showLegend = true;
            labelsToShow = 33;
            chartRadius = ['20%', '45%'];
            chartCenter = ['50%', '33%'];
        }

        if (width === 1 && !chartSettingsView) {
            chartRadius = ['20%', '45%'];
            labelsToShow = 15;
        }

        if (width === 1 && height === 1 && !chartSettingsView) {
            showLegend = chartSettingsView;
        }

        if (width > 1 && height === 1 && !chartSettingsView) {
            chartCenter = ['50%', '33%'];
        }

        if (height === 2) {
            labelsToShow = 33;
        }

        if (height === 3) {
            labelsToShow = 40;
        }

        if (data.length > 15) {
            startAngle = 90;
            fontSize = 10;
            chartRadius = ['20%', '40%'];
        }
        if (data.length > 25) {
            startAngle = 90;
            fontSize = 9;
            labelsToShow = 33;
            chartRadius = ['20%', '30%'];
        }

        return {showLegend, chartRadius, chartCenter, labelsToShow, startAngle, fontSize};
    }, [height, width, chartSettingsView, data.length]);

    const showValues = dataLabelPosition !== DataLabelPosition.Off;
    const usesInsideValueOverlay = pieChartUsesInsideValueOverlay(dataLabelPosition);
    const pieLabelLineConfig = buildPieLabelLineConfig();

    const seriesData = useMemo(() => {
        const buildOutsideLabel = usesInsideValueOverlay || !showValues
            ? buildPieOutsideNameLabelConfig
            : buildPieOutsideNameAndValueLabelConfig;

        return data.map((item, index) => ({
            ...item,
            label: buildOutsideLabel(fontSize, index < labelsToShow),
        }));
    }, [data, fontSize, labelsToShow, showValues, usesInsideValueOverlay]);

    const insideValueSeriesData = useMemo(() => {
        if (!usesInsideValueOverlay) {
            return null;
        }

        return data.map((item, index) => ({
            value: item.value,
            name: item.name,
            label: buildPieInsideValueLabelConfig(fontSize, index < labelsToShow),
        }));
    }, [data, fontSize, labelsToShow, usesInsideValueOverlay]);

    const customTooltip = (params: any) => {
        const {data} = params;

        return `
            <div>
                <strong>${data.name}: ${data.value}</strong><br/>
                ${i18n.t('share_txt.text')} ${data.share}%<br/>
                ${i18n.t('ch_total_label.text')} ${data.total}
            </div>
        `;
    };

    useEffect(() => {
        if (!chartRef.current) {return;}
        
        const piechart = echarts.init(chartRef.current);
        const handleResize = () => {
            if (piechart && !piechart.isDisposed()) {
                piechart.resize();
            }
        };

        const handleChatSidebarChange = () => {
            setTimeout(handleResize, 350);
        };

        let resizeObserver: any = null;
        if (typeof window !== 'undefined' && 'ResizeObserver' in window && chartRef.current) {
            resizeObserver = new (window as any).ResizeObserver(() => {
                handleResize();
            });
            resizeObserver.observe(chartRef.current);
        }

        window.addEventListener('resize', handleResize);
        window.addEventListener('chatSidebarOpened', handleChatSidebarChange);
        window.addEventListener('chatSidebarClosed', handleChatSidebarChange);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('chatSidebarOpened', handleChatSidebarChange);
            window.removeEventListener('chatSidebarClosed', handleChatSidebarChange);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            if (piechart && !piechart.isDisposed()) {
                piechart.dispose();
            }
        }
    }, []);

    useEffect(() => {
        if (!chartRef.current) {return;}

        const chartInstance = echarts.getInstanceByDom(chartRef.current);
        if (!chartInstance || chartInstance.isDisposed()) {return;}

        chartInstance.resize();
    }, [height, width]);

    useEffect(() => {
        if (!chartRef.current) {return;}

        const chartInstance = echarts.getInstanceByDom(chartRef.current);
        if (!chartInstance || chartInstance.isDisposed()) {return;}

        const option = {
            textStyle: {
                fontFamily: '"Rubik", sans-serif',
                fontSize: fontSize,
                fontWeight: 100,
            },
            tooltip: {
                trigger: 'item',
                formatter: customTooltip,
            },
            toolbox: {
                show: showToolbox,
                orient: 'vertical',
                itemSize: 12,
                itemGap: 8,
                feature: {
                    dataView: {
                        optionToContent: function (opt: any) {
                            const series = opt.series[0].data;
                            const headerColumns = '<td>' + i18n.t('jt_value.text') + '</td>';
                            let table =
                                '<table class="table table-hover g-table w-100" style="font-size:0.8em">' +
                                '<thead><tr>' +
                                '<td>' + i18n.t('ch_datapoint.text') + '</td>' +
                                headerColumns +
                                '</tr><thead><tbody>';
                            for (let i = 0, l = series.length - 1; i < l; i++) {
                                table += '<tr><td>' + series[i].name + '</td><td>' + series[i].value + '</td></tr>';
                            }
                            table += '</tbody></table>';
                            return table;
                        },
                        readOnly: true,
                    },
                    ...buildChartImageDownloadToolboxFeatures({
                        title,
                        description,
                        datePeriod,
                        getChartInstance: () => (
                            chartRef.current ? echarts.getInstanceByDom(chartRef.current) : null
                        ),
                    }, showToolbox),
                }
            },
            ...(showLegend && {
                legend: {
                    type: 'scroll',
                    orient: 'horizontal',
                    width: '60%',
                    bottom: '10%',
                    data: seriesData.map((item) => item.name),
                },
            }),
            series: [
                {
                    type: 'pie',
                    id: 'pie-main',
                    radius: chartRadius,
                    center: chartCenter,
                    data: seriesData,
                    emphasis: {
                        itemStyle: {
                            shadowBlur: 10,
                            shadowOffsetX: 0,
                            shadowColor: 'rgba(0, 0, 0, 0.5)',
                        },
                    },
                    clockwise: true,
                    startAngle: startAngle,
                    avoidLabelOverlap: true,
                    stillShowZeroSum: false,
                    labelLine: pieLabelLineConfig,
                },
                ...(insideValueSeriesData ? [{
                    type: 'pie',
                    id: 'pie-inside-values',
                    radius: chartRadius,
                    center: chartCenter,
                    startAngle: startAngle,
                    clockwise: true,
                    silent: true,
                    animation: false,
                    avoidLabelOverlap: true,
                    stillShowZeroSum: false,
                    itemStyle: {
                        color: 'transparent',
                        borderColor: 'transparent',
                        borderWidth: 0,
                    },
                    emphasis: {
                        scale: false,
                        itemStyle: {
                            color: 'transparent',
                            borderColor: 'transparent',
                            borderWidth: 0,
                        },
                    },
                    labelLine: {show: false},
                    data: insideValueSeriesData,
                }] : []),
            ],
        };

        chartInstance.setOption(option, {replaceMerge: ['series']});

    }, [seriesData, insideValueSeriesData, showLegend, chartRadius, chartCenter, startAngle, showToolbox, title, description, datePeriod]);

    return <div ref={chartRef} style={{width: '100%', height: '100%'}}/>;
};

export default PieCharts;
